import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import type { OfficeLayout } from './layout';
import { officeDoorFrame } from './officeDoor';

/** Small deterministic relief, not baked lighting or a photograph. */
function grain(): THREE.DataTexture {
  const data = new Uint8Array(64 * 64 * 4);
  for (let i = 0; i < 64 * 64; i++) {
    const value = 115 + ((i * 73 + Math.floor(i / 64) * 19) % 31);
    data.set([value, value, value, 255], i * 4);
  }
  const texture = new THREE.DataTexture(data, 64, 64);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(12, 8);
  texture.needsUpdate = true;
  return texture;
}

/** One-room finish only. No changes to world IDs, NPCs, paths or collision proxies. */
export function finishIsOffice(root: THREE.Group, layout: OfficeLayout): void {
  const room = layout.rooms['is-office'];
  if (!room) return;
  const finish = new THREE.Group(); finish.name = 'is-office-finish';
  root.add(finish);
  const { x0, x1, z0, z1 } = room.rect;
  const y = room.y;
  const plaster = new THREE.MeshStandardMaterial({ color: 0xd6d2c9, roughness: 0.94,
    bumpMap: grain(), bumpScale: 0.0012 });
  const aluminium = new THREE.MeshStandardMaterial({ color: 0x929b9e, metalness: 0.72, roughness: 0.34 });
  const paint = new THREE.MeshStandardMaterial({ color: 0xd9dcd8, metalness: 0.12, roughness: 0.48 });
  const dark = new THREE.MeshStandardMaterial({ color: 0x414c50, roughness: 0.72 });
  const wood = new THREE.MeshStandardMaterial({ color: 0xbba78a, roughness: 0.7 });
  wood.userData.oak = true;
  function box(name: string, size: [number, number, number], position: [number, number, number], material: THREE.MeshStandardMaterial) {
    const mesh = new THREE.Mesh(new RoundedBoxGeometry(...size, 1, Math.min(0.004, ...size.map(v => v / 4))), material);
    mesh.name = name; mesh.position.set(...position);
    mesh.castShadow = true; mesh.receiveShadow = true;
    finish.add(mesh); return mesh;
  }
  // Side finish lies inside the existing 0.15m wall surface, extending only 0.025m into the room.
  box('it-plaster-right', [0.02, 2.98, room.depth - 0.34], [x1 - 0.16, y + 1.61, room.center[2]], plaster);
  // Shallow joinery, below/above the existing city-view plane; do not cover the exterior image.
  const windowWidth = room.width - 1.1;
  box('it-window-sill', [windowWidth + 0.12, 0.045, 0.07], [room.center[0], y + 1.045, z0 + 0.19], paint);
  box('it-window-pelmet', [windowWidth + 0.14, 0.18, 0.055], [room.center[0], y + 2.89, z0 + 0.18], paint);
  for (const side of [-1, 1]) {
    box('it-window-reveal', [0.06, 1.73, 0.06], [room.center[0] + side * (windowWidth / 2 + 0.04), y + 1.92, z0 + 0.18], plaster);
  }
  // Cabinet body/collider remains untouched. Door seams and recessed plinth give scale.
  const cabinetWidth = Math.min(2.4, windowWidth);
  box('it-cabinet-plinth', [cabinetWidth - 0.06, 0.065, 0.016], [room.center[0], y + 0.055, z0 + 0.309], dark);
  box('it-cabinet-top', [cabinetWidth, 0.025, 0.22], [room.center[0], y + 0.808, z0 + 0.195], paint);
  for (const offset of [-0.25, 0, 0.25]) {
    box('it-cabinet-joint', [0.006, 0.65, 0.006], [room.center[0] + offset * cabinetWidth, y + 0.44, z0 + 0.308], dark);
  }
  // Low-profile service rail: a believable cable route, not a fictitious network link.
  box('it-service-rail', [0.03, 0.065, room.depth - 0.45], [x1 - 0.17, y + 0.36, room.center[2]], paint);
  for (const z of [z0 + room.depth * 0.3, z0 + room.depth * 0.7]) {
    box('it-service-cover', [0.012, 0.045, 0.08], [x1 - 0.192, y + 0.36, z], aluminium);
  }
  // Door is intentionally open: dress the existing opening without a blocking door leaf.
  const doorFaceZ = officeDoorFrame(room).faceZ;
  for (const side of [-1, 1]) {
    box('it-door-architrave', [0.045, 2.78, 0.025], [room.doorPosition[0] + side * 1.16, y + 1.39, doorFaceZ], aluminium);
  }
  box('it-door-header', [2.365, 0.055, 0.025], [room.doorPosition[0], y + 2.79, doorFaceZ], aluminium);
  // A restrained timber strip ties the new joinery to the existing desks.
  box('it-wall-timber', [0.025, 0.19, room.depth - 0.45], [x1 - 0.177, y + 2.94, room.center[2]], wood);
  const ceiling = root.getObjectByName(`ceiling:${room.rect.id}`) as THREE.Mesh | undefined;
  if (ceiling?.material instanceof THREE.MeshStandardMaterial) {
    ceiling.material.bumpMap = grain(); ceiling.material.bumpScale = 0.0007;
    ceiling.material.color.setHex(0xe9e7de);
  }
  // Store the room bounds for audits; this group is never an interactable.
  finish.userData.roomId = room.id;
  finish.userData.bounds = [x0, x1, z0, z1];
}
